import { isVariant, variantDisplayName } from '@/lib/variants';
import { roundMoney } from '@/lib/pricing';

export interface PriceAdjustProduct {
  id: string;
  name?: string | null;
  price?: number | null;
  cost?: number | string | null;
  parent_product_id?: string | null;
  variant_name?: string | null;
  category?: string | null;
}

export interface PriceAdjustUpdate {
  id: string;
  displayName: string;
  previousPrice: number;
  price: number;
  previousCost: number | null;
  cost: number | null;
  isParent: boolean;
}

export interface PriceAdjustPlan {
  updates: PriceAdjustUpdate[];
}

export interface PriceAdjustInput {
  products: PriceAdjustProduct[];
  selectedCategory: string;
  adjustmentType: 'increase' | 'decrease';
  percentValue: number;
}

/**
 * Monta o plano de reajuste de preço/custo em massa.
 *
 * REGRAS DO PRODUTO:
 * 1. O reajuste em massa se aplica a produtos simples e variações de produtos.
 * 2. Em produtos com variações (pai), o reajuste desce para as filhas:
 *    - Os preços e custos (se informados) das variações são reajustados.
 *    - O preço do pai é atualizado para o mínimo dos novos preços das variações.
 *    - O custo do pai permanece null (decisão do grupo 1).
 * 3. Se um item simples ou variação não possuir custo informado (cost === null),
 *    o ajuste percentual de custo é ignorado (não grava zero).
 */
export function buildBulkPriceAdjustPlan({
  products,
  selectedCategory,
  adjustmentType,
  percentValue,
}: PriceAdjustInput): PriceAdjustPlan {
  const multiplier = adjustmentType === 'increase'
    ? 1 + (percentValue / 100)
    : 1 - (percentValue / 100);

  const list = products ?? [];

  // Agrupa as variações por ID do produto pai
  const variantsByParent = new Map<string, PriceAdjustProduct[]>();
  for (const p of list) {
    if (isVariant(p)) {
      const key = String(p.parent_product_id);
      const bucket = variantsByParent.get(key) || [];
      bucket.push(p);
      variantsByParent.set(key, bucket);
    }
  }

  // Encontra os produtos raízes que pertencem à categoria selecionada
  const matchingRoots = list.filter((p) => {
    if (isVariant(p)) return false;
    if (selectedCategory !== 'all' && p.category !== selectedCategory) return false;
    return true;
  });

  const updates: PriceAdjustUpdate[] = [];

  for (const root of matchingRoots) {
    const parentId = String(root.id);
    const variants = variantsByParent.get(parentId) || [];

    if (variants.length > 0) {
      // Tem variações: atualiza cada filha
      const newPrices: number[] = [];
      const parentName = root.name ?? '';

      for (const variant of variants) {
        const prevPrice = Number(variant.price ?? 0);
        const newPrice = Math.max(0.01, roundMoney(prevPrice * multiplier));
        newPrices.push(newPrice);

        const prevCost = variant.cost != null && variant.cost !== '' ? Number(variant.cost) : null;
        let newCost: number | null = null;
        if (prevCost != null) {
          newCost = Math.max(0, roundMoney(prevCost * multiplier));
        }

        updates.push({
          id: String(variant.id),
          displayName: variantDisplayName(parentName, variant.variant_name),
          previousPrice: prevPrice,
          price: newPrice,
          previousCost: prevCost,
          cost: newCost,
          isParent: false,
        });
      }

      // O preço do pai é o mínimo dos preços das suas filhas
      const newParentPrice = Math.min(...newPrices);
      const prevParentPrice = Number(root.price ?? 0);
      updates.push({
        id: parentId,
        displayName: String(root.name ?? ''),
        previousPrice: prevParentPrice,
        price: newParentPrice,
        previousCost: null,
        cost: null, // custo do pai sempre nulo
        isParent: true,
      });
    } else {
      // Produto simples
      const prevPrice = Number(root.price ?? 0);
      const newPrice = Math.max(0.01, roundMoney(prevPrice * multiplier));

      const prevCost = root.cost != null && root.cost !== '' ? Number(root.cost) : null;
      let newCost: number | null = null;
      if (prevCost != null) {
        newCost = Math.max(0, roundMoney(prevCost * multiplier));
      }

      updates.push({
        id: parentId,
        displayName: String(root.name ?? ''),
        previousPrice: prevPrice,
        price: newPrice,
        previousCost: prevCost,
        cost: newCost,
        isParent: false,
      });
    }
  }

  return { updates };
}
